<template>
  <Dialog
    :header="headerText"
    :visible.sync="show"
    :style="{ width: '520px' }"
    :modal="true"
    :closable="!inProgress"
  >
    <div class="enquiry-save-progress">
      <!-- Line 1 — the committed transaction: enquiry + notes + history -->
      <div class="progress-line">
        <i :class="saveIcon" :style="{ color: saveColour }" />
        <span class="progress-label">{{ saveLabel }}</span>
        <span v-if="saveState === 'saved' && quoteNo" class="quote-badge">{{ quoteNo }}</span>
      </div>

      <!-- Line 2 — the notification email, sent after the enquiry was committed -->
      <div class="progress-line">
        <i :class="emailIcon" :style="{ color: emailColour }" />
        <span class="progress-label" :style="{ color: emailColour }">{{ emailLabel }}</span>
      </div>

      <p v-if="saveState === 'saved'" class="reassurance">
        This enquiry has been saved and will not be lost.
      </p>
    </div>

    <template #footer>
      <Button
        label="Ok"
        icon="pi pi-check"
        class="p-button-text"
        :disabled="inProgress"
        autofocus
        @click="$emit('close')"
      />
      <Button
        v-if="saveState === 'saved'"
        label="Book In"
        :disabled="inProgress"
        @click="$emit('book-in')"
      />
    </template>
  </Dialog>
</template>

<script>
const GREEN = "#1f9254";
const AMBER = "#c77700";
const RED = "#c62828";
const GREY = "#6c757d";

export default {
  name: "EnquirySaveProgress",
  props: {
    visible: { type: Boolean, default: false },
    /** 'pending' while saving, 'saved' once committed, 'error' if it never saved. */
    saveState: { type: String, default: "pending" },
    /** 'pending', or the backend's emailStatus: 'SENT' | 'SKIPPED' | 'FAILED'. */
    emailState: { type: String, default: "pending" },
    /** The allocated enquiry number, e.g. "Q54153". */
    quoteNo: { type: String, default: "" }
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) this.$emit("close");
      }
    },
    inProgress() {
      return this.saveState === "pending";
    },
    headerText() {
      if (this.saveState === "pending") return "Saving Enquiry";
      if (this.saveState === "error") return "Enquiry Not Saved";
      return "Enquiry Saved";
    },
    saveLabel() {
      if (this.saveState === "pending") return "Saving enquiry...";
      if (this.saveState === "error") return "Enquiry could not be saved";
      return "Enquiry Saved";
    },
    saveIcon() {
      if (this.saveState === "pending") return "pi pi-spin pi-spinner";
      if (this.saveState === "error") return "pi pi-times-circle";
      return "pi pi-check-circle";
    },
    saveColour() {
      if (this.saveState === "pending") return GREY;
      if (this.saveState === "error") return RED;
      return GREEN;
    },
    emailLabel() {
      if (this.saveState === "error") return "No notifications sent";
      switch (this.emailState) {
        case "SENT":
          return "Emails Sent";
        case "SKIPPED":
          // Emails are deliberately switched off outside LIVE.
          return "Emails not enabled in this environment";
        case "FAILED":
          return "Enquiry saved. Notification email could not be sent.";
        default:
          return "Sending notification emails...";
      }
    },
    emailIcon() {
      if (this.saveState === "error") return "pi pi-minus-circle";
      switch (this.emailState) {
        case "SENT":
          return "pi pi-check-circle";
        case "SKIPPED":
          return "pi pi-info-circle";
        case "FAILED":
          return "pi pi-exclamation-triangle";
        default:
          return "pi pi-spin pi-spinner";
      }
    },
    emailColour() {
      if (this.saveState === "error") return GREY;
      switch (this.emailState) {
        case "SENT":
          return GREEN;
        case "SKIPPED":
          return GREY;
        case "FAILED":
          return AMBER;
        default:
          return GREY;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.enquiry-save-progress {
  padding: 0.5rem 0.25rem;
}

.progress-line {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0;
  font-size: 1.05rem;

  i {
    font-size: 1.35rem;
    flex: 0 0 auto;
  }
}

.progress-label {
  flex: 1 1 auto;
}

.quote-badge {
  flex: 0 0 auto;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: #eef4ee;
  color: #1f9254;
  border-radius: 4px;
  padding: 0.15rem 0.5rem;
}

.reassurance {
  margin: 0.75rem 0 0;
  font-size: 0.85rem;
  color: #6c757d;
}
</style>
